import { Config } from '../core/Config';
import { injectStyles } from '../utils/injectStyles';
import { lockBodyScroll, unlockBodyScroll } from '../utils/scrollLock';

/**
 * Base class for UI components
 */
export abstract class BaseComponent {
    protected container: HTMLElement | null = null;
    protected config: Config;
    private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
    private scrollLocked: boolean = false;

    constructor(config: Config) {
        this.config = config;
    }

    protected abstract render(): HTMLElement;

    /**
     * Full-screen modal components (Popup, AnnouncementList) override this to
     * return true so the host page's scroll is locked while they are visible.
     * Non-modal components (top bars, toasts) leave it false. The shared,
     * reference-counted lock keeps the page frozen across a list → popup → list
     * hand-off with no unlock gap.
     */
    protected lockScroll(): boolean {
        return false;
    }

    public show(): void {
        if (this.container) {
            return;
        }

        // Acquire the scroll lock before rendering so a render failure below is
        // unwound by the catch — otherwise a throw could leave the host page
        // permanently locked and unusable.
        if (this.lockScroll()) {
            lockBodyScroll();
            this.scrollLocked = true;
        }

        try {
            this.container = this.render();
            document.body.appendChild(this.container);
            injectStyles();
            this.attachEscapeListener();
        } catch (err) {
            this.releaseScrollLock();
            throw err;
        }
    }

    public hide(): void {
        this.detachEscapeListener();

        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        this.container = null;

        this.releaseScrollLock();
    }

    private releaseScrollLock(): void {
        if (this.scrollLocked) {
            this.scrollLocked = false;
            unlockBodyScroll();
        }
    }

    /**
     * Subclasses opt in to Escape-to-close by overriding this to return true.
     * The listener is attached in show() and detached in hide() so it never
     * outlives the component (previous behavior leaked handlers when the
     * component closed via any path other than Escape itself).
     */
    protected closeOnEscape(): boolean {
        return false;
    }

    /**
     * Hook called when the user presses Escape while the component is visible.
     */
    protected onEscape(): void {
        this.hide();
    }

    private attachEscapeListener(): void {
        if (!this.closeOnEscape() || this.escapeHandler) {
            return;
        }

        this.escapeHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.onEscape();
            }
        };

        document.addEventListener('keydown', this.escapeHandler);
    }

    private detachEscapeListener(): void {
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
            this.escapeHandler = null;
        }
    }

    public isVisible(): boolean {
        return this.container !== null && document.body.contains(this.container);
    }

    protected createElement<K extends keyof HTMLElementTagNameMap>(
        tag: K,
        className?: string,
        attributes?: { [key: string]: string }
    ): HTMLElementTagNameMap[K] {
        const element = document.createElement(tag);

        if (className) {
            element.className = className;
        }

        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                element.setAttribute(key, value);
            });
        }

        return element;
    }

    protected formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);

            // Validate date
            if (isNaN(date.getTime())) {
                return dateString;
            }

            const now = new Date();
            const diffMs = now.getTime() - date.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            // Handle future dates or today (including recently published)
            if (diffDays <= 0) {
                return this.config.t.today;
            } else if (diffDays === 1) {
                return this.config.t.yesterday;
            } else if (diffDays < 7) {
                return this.config.t.daysAgo.replace('{days}', diffDays.toString());
            } else {
                // Format date in user's local timezone with locale-specific formatting
                return date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
            }
        } catch {
            return dateString;
        }
    }
}
